import { IconButton, Stack, TextField } from '@mui/material'
import { useEffect, useReducer, type KeyboardEvent } from 'react'
import {
  counterReducer,
  counterView,
  initCounter,
  type CounterAction,
  type CounterRules,
  type CounterState,
} from '@commerce/core'

interface Props {
  value: number
  rules: CounterRules
  onCommit: (value: number) => void
}

/**
 * The stepper. It contains **zero arithmetic**: every number and every
 * enabled/disabled decision comes from `counterView` or `counterReducer` in
 * `@commerce/core`. Replaces `Counter.tsx`'s 319 LOC of
 * `useState`/`useMemo`/`useEffect`.
 *
 * Two departures from the plan's sketch:
 *
 * - `slotProps.htmlInput` rather than the deprecated `inputProps`, which is the
 *   MUI 6 spelling.
 * - `role="spinbutton"` carries `aria-valuenow` / `aria-valuemin` /
 *   `aria-valuemax`. The role on its own announces a spinner a screen reader
 *   cannot describe, which is worse than leaving it a textbox. `aria-valuenow`
 *   tracks the *committed* value, not the draft, because a half-typed number is
 *   not the quantity yet.
 */
export const QuantityStepper = ({ value, rules, onCommit }: Props) => {
  const [state, dispatch] = useReducer(
    (s: CounterState, a: CounterAction) => counterReducer(s, a, rules),
    initCounter(value),
  )

  // Adopt server-confirmed quantities without interrupting typing.
  useEffect(() => {
    dispatch({ type: 'syncExternal', value })
  }, [value])

  const view = counterView(state, rules)

  /**
   * Apply an action and report only a real change. `next` is computed from the
   * current state rather than read back after `dispatch`, because a reducer
   * update is not visible until the next render.
   */
  const apply = (action: CounterAction) => {
    const next = counterReducer(state, action, rules)
    dispatch(action)
    if (next.value !== state.value) onCommit(next.value)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      apply({ type: 'commitDraft' })
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      dispatch({ type: 'cancelDraft' })
    }
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <IconButton
        aria-label="decrease quantity"
        disabled={!view.canDecrement}
        onClick={() => apply({ type: 'decrement' })}
      >
        −
      </IconButton>
      <TextField
        size="small"
        value={view.text}
        onChange={(e) => dispatch({ type: 'typeDraft', raw: e.target.value })}
        onBlur={() => apply({ type: 'commitDraft' })}
        onKeyDown={onKeyDown}
        sx={{ width: 72 }}
        slotProps={{
          htmlInput: {
            'role': 'spinbutton',
            'aria-label': 'quantity',
            'aria-valuenow': view.value,
            'aria-valuemin': rules.min,
            'aria-valuemax': rules.max,
            'inputMode': 'numeric',
            'style': { textAlign: 'center' },
          },
        }}
      />
      <IconButton
        aria-label="increase quantity"
        disabled={!view.canIncrement}
        onClick={() => apply({ type: 'increment' })}
      >
        +
      </IconButton>
    </Stack>
  )
}
